import { FC, ReactNode, useEffect } from 'react';

import {
	IMplSnappingBottomSheetProps,
	MplSnappingBottomSheet,
} from '../../../../molecules/MplSnappingBottomSheet';
import { useSearchFlowLayoutStore } from '../../stores/useSearchFlowLayoutStore';

import { ActionsRow, HeaderComposition } from './styled';

export interface ISearchFlowBottomSheetProps
	extends Pick<
		IMplSnappingBottomSheetProps,
		| 'snapPoints'
		| 'elevatedFooter'
		| 'animateOnMount'
		| 'open'
		| 'unmountOnClose'
		| 'zIndex'
		| 'dragHandleTestId'
	> {
	/** Initial active snap index (controlled) */
	initialSnapIndex?: number;
	/** Action chips pinned to the left of the header row */
	leftHeaderActions?: ReactNode;
	/** Action chips pinned to the right of the header row */
	rightHeaderActions?: ReactNode;
	/** Horizontal row of tab buttons — rendered below actions in the header */
	tabs?: ReactNode;
	/** Main scrollable content */
	children: ReactNode;
	/** Bottom action button */
	actionButton?: ReactNode;
	/** className forwarded to the underlying MplSnappingBottomSheet */
	className?: string;
}

export const SearchFlowBottomSheet: FC<ISearchFlowBottomSheetProps> = ({
	snapPoints,
	initialSnapIndex = 0,
	leftHeaderActions,
	rightHeaderActions,
	tabs,
	children,
	actionButton,
	elevatedFooter,
	animateOnMount = true,
	open = true,
	unmountOnClose = false,
	zIndex,
	className,
	dragHandleTestId,
}) => {
	const { currentSnapIndex, setSnapIndex } = useSearchFlowLayoutStore();

	/** Initialize snap index */
	useEffect(() => {
		setSnapIndex(initialSnapIndex);
	}, [initialSnapIndex, setSnapIndex]);

	const hasActions = leftHeaderActions || rightHeaderActions;

	/** header is undefined when all slots are absent — prevents DrawerHeaderSlot from mounting */
	const header =
		hasActions || tabs ? (
			<HeaderComposition>
				{hasActions && (
					<ActionsRow>
						{leftHeaderActions}
						{rightHeaderActions}
					</ActionsRow>
				)}
				{tabs}
			</HeaderComposition>
		) : undefined;

	return (
		<MplSnappingBottomSheet
			snapPoints={snapPoints}
			currentSnapIndex={currentSnapIndex}
			onSnapIndexChange={setSnapIndex}
			header={header}
			footer={actionButton}
			elevatedFooter={elevatedFooter}
			animateOnMount={animateOnMount}
			open={open}
			unmountOnClose={unmountOnClose}
			zIndex={zIndex}
			className={className}
			dragHandleTestId={dragHandleTestId}
		>
			{children}
		</MplSnappingBottomSheet>
	);
};
